import { has, includes, invert } from 'lodash-es';

import { hasWindow } from '../src/utils/window';
import type { Error as ErrorDTO } from './@types/schema';
import { ResponseStatus } from './@types/schema';
import type { Values } from './Logger';
import { defaultLogLevelMap } from './Logger';
import { isRecordType } from './typegaurd';

export type ErrorInfo = {
  status: Response['status'];
  url: string;
  handled: boolean;
  extra?: unknown;
};

type AvailableLogLevel = Values<typeof defaultLogLevelMap>;

type LogMessageInfo = {
  level: keyof typeof defaultLogLevelMap;
  message: string;
};

const isAvailableLogLevel = (customLogLevel?: number): customLogLevel is AvailableLogLevel =>
  typeof customLogLevel === 'number' && includes(Object.values(defaultLogLevelMap), customLogLevel);

export class ApiError extends Error {
  readonly statusCode: ErrorDTO['statusCode'];

  constructor(
    private readonly errorDTO: ErrorDTO,
    private readonly info: ErrorInfo,
  ) {
    super(errorDTO.message);
    this.statusCode = this.errorDTO.statusCode;
  }

  get errorCode() {
    return this.errorDTO.errorCode;
  }

  static createError(errorDTO: ErrorDTO, errorInfo: ErrorInfo, customLogLevel?: number) {
    if (isAvailableLogLevel(customLogLevel)) {
      return new CustomLevelError(errorDTO, errorInfo, customLogLevel);
    }

    const infoLevelErrorList = [ResponseStatus.NOT_FOUND, ResponseStatus.UNAUTHORIZED];

    if (infoLevelErrorList.some((status) => status === errorDTO.statusCode)) {
      return new InfoLevelError(errorDTO, errorInfo);
    }

    return new ApiError(errorDTO, errorInfo);
  }

  protected isErrorResponse(response: unknown): response is ErrorDTO {
    return ApiError.isErrorResponse(response);
  }

  static isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
  }

  static isErrorResponse(response: unknown): response is ErrorDTO {
    return (
      isRecordType(response) &&
      has(response, 'data') &&
      has(response, 'statusCode') &&
      has(response, 'message') &&
      response.statusCode !== ResponseStatus.OK
    );
  }

  static is(response: unknown, statusCode: ErrorDTO['statusCode']): response is ApiError {
    return ApiError.isApiError(response) && response.errorDTO.statusCode === statusCode;
  }

  static isBadRequest(statusCode: ErrorDTO['statusCode']) {
    return statusCode === ResponseStatus.BAD_REQUEST;
  }

  static isTooManyRequest(statusCode: ErrorDTO['statusCode']) {
    return statusCode === ResponseStatus.TOO_MANY_REQUESTS;
  }

  static isForbidden(error: unknown) {
    return ApiError.is(error, ResponseStatus.FORBIDDEN);
  }

  static isConflict(error: unknown) {
    return ApiError.is(error, ResponseStatus.CONFLICT);
  }

  static isNotFound(error: unknown) {
    return ApiError.is(error, ResponseStatus.NOT_FOUND);
  }

  /**
   * @virtual
   */
  logMessage(): LogMessageInfo {
    const { status, handled, url } = this.info;
    const prefix = handled ? 'HANDLED' : 'UNHANDLED';
    const statusCodeWithMessage = this.isErrorResponse(this.info.extra)
      ? `${this.info.extra.statusCode}: ${this.info.extra.message}`
      : '';
    const body = `${status} error ${statusCodeWithMessage}`;
    const message = `fetch: [${prefix} ${body}] *url: ${url} *data: ${
      this.info.extra ? JSON.stringify(this.info.extra) : ''
    } *release: ${hasWindow() ? window.__RELEASE__ : ''}`;

    if (body.includes('AbortError')) {
      return { level: 'info', message };
    }

    return { level: 'error', message };
  }
}

class InfoLevelError extends ApiError {
  /**
   * @override
   */
  override logMessage(): { level: 'error' | 'info'; message: string } {
    const messageOption = super.logMessage();

    return {
      ...messageOption,
      level: 'info',
    };
  }
}

class CustomLevelError extends ApiError {
  private customLogLevel: AvailableLogLevel;

  constructor(errorDTO: ErrorDTO, errorInfo: ErrorInfo, customLogLevel: AvailableLogLevel) {
    super(errorDTO, errorInfo);
    this.customLogLevel = customLogLevel;
  }

  private isLogLevelKey(key: string): key is keyof typeof defaultLogLevelMap {
    return includes(Object.keys(defaultLogLevelMap), key);
  }

  override logMessage() {
    const messageOption = super.logMessage();
    const level = invert(defaultLogLevelMap)[this.customLogLevel];

    return {
      ...messageOption,
      level: this.isLogLevelKey(level) ? level : 'error',
    };
  }
}
